import React, { useMemo } from 'react';
import { HelpCircle, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { getDriverHelperText } from '../../utils/forecastCalculations';

const DriverHelpPanel = ({
  isOpen,
  onClose,
  driverType,
  rowData = null,
  accountType = ''
}) => {
  // Get troubleshooting messages based on driver and data
  const troubleshooting = useMemo(() => {
    if (!rowData || !driverType) return [];

    const issues = [];

    // Check if forecast amount is zero or null
    const forecastAmount = rowData.forecastAmount || 0;
    const input = rowData.input || 0;
    const availableRooms = rowData.availableRooms || 0;
    const occupiedRooms = rowData.occupiedRooms || 0;
    const budget = rowData.budget || 0;
    const daysWithData = rowData.daysWithData || 0;

    if (forecastAmount === 0 && driverType !== '') {
      // Driver-specific troubleshooting
      switch (driverType) {
        case '$ / ARN':
        case '$ / ARN LM':
          if (availableRooms === 0) {
            issues.push({
              type: 'error',
              message: 'No room data imported for this month',
              solution: 'Import hotel room statistics or switch to a different driver (e.g., Budget, Static Entry)'
            });
          } else if (input === 0 && driverType === '$ / ARN') {
            issues.push({
              type: 'warning',
              message: 'Driver input value is $0',
              solution: 'Enter a rate per available room night in the Input column'
            });
          } else if (daysWithData === 0) {
            issues.push({
              type: 'warning',
              message: 'No daily room data for pace calculation',
              solution: 'Import daily room statistics for more accurate projections'
            });
          }
          break;

        case '$ / ORN':
        case '$ / ORN LM':
          if (occupiedRooms === 0) {
            issues.push({
              type: 'error',
              message: 'No occupied room data for this month',
              solution: 'Import hotel room statistics or switch to a different driver'
            });
          } else if (input === 0 && driverType === '$ / ORN') {
            issues.push({
              type: 'warning',
              message: 'Driver input value is $0',
              solution: 'Enter a rate per occupied room night in the Input column'
            });
          }
          break;

        case 'Budget':
          if (budget === 0) {
            issues.push({
              type: 'error',
              message: 'No budget configured for this account',
              solution: 'Import budget data or switch to Static Entry to enter forecast manually'
            });
          }
          break;

        case 'Static Entry':
          if (input === 0) {
            issues.push({
              type: 'info',
              message: 'No forecast value entered',
              solution: 'Enter forecast amounts in the Input column for each month'
            });
          }
          break;

        case 'OTB Forecast':
        case 'RMS Forecast':
          issues.push({
            type: 'error',
            message: 'No forecast revenue data imported from hotel system',
            solution: 'Import occupancy forecast data from your hotel PMS/RMS'
          });
          break;

        case 'REVPAR Projection':
          if (availableRooms === 0) {
            issues.push({
              type: 'error',
              message: 'No room data available',
              solution: 'Import hotel room statistics'
            });
          } else if (input === 0) {
            issues.push({
              type: 'warning',
              message: 'No RevPAR value entered',
              solution: 'Enter target RevPAR in the Input column'
            });
          }
          break;

        case '$ LM':
          issues.push({
            type: 'info',
            message: 'No previous month data available',
            solution: 'Wait for previous month to have actual or forecast data'
          });
          break;

        default:
          break;
      }
    }

    return issues;
  }, [rowData, driverType]);

  // Get worked example with actual data
  const workedExample = useMemo(() => {
    if (!rowData || !driverType) return null;

    const input = rowData.input || 0;
    const availableRooms = rowData.availableRooms || 0;
    const occupiedRooms = rowData.occupiedRooms || 0;
    const budget = rowData.budget || 0;
    const forecastAmount = rowData.forecastAmount || 0;
    const daysWithData = rowData.daysWithData || 0;
    const daysInMonth = new Date(new Date().getFullYear(), rowData.monthIndex + 1, 0).getDate();

    switch (driverType) {
      case '$ / ARN':
        if (daysWithData > 0 && availableRooms > 0) {
          const avgDailyRooms = (availableRooms / daysWithData).toFixed(2);
          const projectedRooms = (avgDailyRooms * daysInMonth).toFixed(0);
          return `($${input.toFixed(2)}/room × ${avgDailyRooms} avg daily rooms × ${daysInMonth} days) = $${forecastAmount.toLocaleString()}`;
        }
        return `$${input.toFixed(2)}/room × ${availableRooms} rooms = $${forecastAmount.toLocaleString()}`;

      case '$ / ORN':
        if (daysWithData > 0 && occupiedRooms > 0) {
          const avgDailyOccupied = (occupiedRooms / daysWithData).toFixed(2);
          const projectedOccupied = (avgDailyOccupied * daysInMonth).toFixed(0);
          return `($${input.toFixed(2)}/room × ${avgDailyOccupied} avg daily occupied × ${daysInMonth} days) = $${forecastAmount.toLocaleString()}`;
        }
        return `$${input.toFixed(2)}/room × ${occupiedRooms} occupied = $${forecastAmount.toLocaleString()}`;

      case 'Budget':
        return `Budget amount = $${budget.toLocaleString()}`;

      case 'Static Entry':
        return `Input value = $${input.toLocaleString()}`;

      case 'REVPAR Projection':
        return `$${input.toFixed(2)} RevPAR × ${availableRooms} available rooms = $${forecastAmount.toLocaleString()}`;

      case '% of Revenue':
        const pct = (input * 100).toFixed(2);
        return `${pct}% of total revenue`;

      case 'Occupancy Forecast':
        return `$${input.toFixed(2)}/room × ${occupiedRooms} occupied = $${forecastAmount.toLocaleString()}`;

      case '$ LM':
        return `Previous month's actual/forecast = $${forecastAmount.toLocaleString()}`;

      case 'OTB Forecast':
        return `On-The-Books revenue from hotel system = $${forecastAmount.toLocaleString()}`;

      case 'RMS Forecast':
        return `Revenue Management System projection = $${forecastAmount.toLocaleString()}`;

      default:
        return null;
    }
  }, [rowData, driverType]);

  // Return null after all hooks are called
  if (!isOpen) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <HelpCircle className="text-blue-600" size={20} />
          <h4 className="font-semibold text-blue-900">
            {driverType || 'Manual Entry'}
          </h4>
        </div>
        <button
          onClick={onClose}
          className="text-blue-600 hover:text-blue-800 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Description */}
      <div className="text-sm text-blue-800">
        <p className="font-medium mb-1">How it works:</p>
        <p>{getDriverHelperText(driverType)}</p>
      </div>

      {/* Worked Example */}
      {workedExample && (
        <div className="bg-white border border-blue-200 rounded p-3">
          <p className="text-xs font-semibold text-blue-700 mb-1">Example Calculation:</p>
          <p className="text-sm text-gray-800 font-mono">{workedExample}</p>
        </div>
      )}

      {/* Troubleshooting */}
      {troubleshooting.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-blue-700">Troubleshooting:</p>
          {troubleshooting.map((issue, index) => (
            <div
              key={index}
              className={`p-3 rounded border ${
                issue.type === 'error'
                  ? 'bg-red-50 border-red-200'
                  : issue.type === 'warning'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-blue-50 border-blue-200'
              }`}
            >
              <div className="flex items-start gap-2">
                {issue.type === 'error' ? (
                  <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={16} />
                ) : issue.type === 'warning' ? (
                  <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={16} />
                ) : (
                  <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    issue.type === 'error'
                      ? 'text-red-800'
                      : issue.type === 'warning'
                      ? 'text-yellow-800'
                      : 'text-blue-800'
                  }`}>
                    {issue.message}
                  </p>
                  <p className={`text-xs mt-1 ${
                    issue.type === 'error'
                      ? 'text-red-700'
                      : issue.type === 'warning'
                      ? 'text-yellow-700'
                      : 'text-blue-700'
                  }`}>
                    💡 {issue.solution}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Documentation Link */}
      <div className="pt-2 border-t border-blue-200">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            // Link to documentation - implement actual link when ready
          }}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          View full documentation →
        </a>
      </div>
    </div>
  );
};

export default DriverHelpPanel;
